import { useMemo, useState } from 'react';
import {
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from 'recharts';

import type { VolumeHealthPoint } from '@/api/overview/mock';
import { Card } from '@/components/ui/card';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useInView } from '@/lib/use-in-view';

// ---------------------------------------------------------------------------
// Shared chart chrome (kept local so this panel is self-contained)
// ---------------------------------------------------------------------------

const AXIS_TICK = { fill: 'var(--fg-subtle)', fontSize: 12 };

const TOOLTIP_STYLE = {
	backgroundColor: 'var(--popover)',
	border: '1px solid var(--border-strong)',
	borderRadius: 8,
	color: 'var(--fg)',
	fontSize: 12,
} as const;

const METRIC_COLORS = {
	sent: 'var(--brand-navy)',
	delivered: 'var(--success)',
	failed: 'var(--danger)',
} as const;

// ---------------------------------------------------------------------------
// Filter option vocab
// ---------------------------------------------------------------------------

const TIME_OF_DAY_OPTIONS = [
	{ value: 'all', label: 'All day' },
	{ value: 'night', label: 'Night (0-6)' },
	{ value: 'morning', label: 'Morning (6-12)' },
	{ value: 'afternoon', label: 'Afternoon (12-18)' },
	{ value: 'evening', label: 'Evening (18-24)' },
] as const;

const DAY_OF_WEEK_OPTIONS = [
	{ value: 'all', label: 'Every day' },
	{ value: '1', label: 'Monday' },
	{ value: '2', label: 'Tuesday' },
	{ value: '3', label: 'Wednesday' },
	{ value: '4', label: 'Thursday' },
	{ value: '5', label: 'Friday' },
	{ value: '6', label: 'Saturday' },
	{ value: '0', label: 'Sunday' },
] as const;

const DATE_RANGE_OPTIONS = [
	{ value: 'week', label: 'Last week' },
	{ value: 'month', label: 'Last month' },
	{ value: 'year', label: 'Last year' },
] as const;

const DATE_RANGE_DAYS: Record<string, number> = {
	week: 7,
	month: 31,
	year: 366,
};

type ApplicationOption = { value: string; label: string };

function bucketOfHour(hour: number): string {
	if (hour < 6) return 'night';
	if (hour < 12) return 'morning';
	if (hour < 18) return 'afternoon';
	return 'evening';
}

interface DailyRow {
	date: string;
	sent: number;
	delivered: number;
	failed: number;
}

/**
 * Single aggregate-over-time panel. Filtering is pure client-side derivation
 * (four `useState`s -> one `useMemo`) over the full `volumeAndHealth` dataset —
 * no network calls. Remaining points are bucketed by calendar day and summed
 * into a line series (sent / delivered / failed).
 */
export function VolumeAndHealthPanel({ data }: { data: VolumeHealthPoint[] }) {
	const [timeOfDay, setTimeOfDay] = useState('all');
	const [dayOfWeek, setDayOfWeek] = useState('all');
	const [dateRange, setDateRange] = useState('month');
	const [application, setApplication] = useState('all');

	const { ref, inView } = useInView<HTMLDivElement>();

	const applicationOptions = useMemo<ApplicationOption[]>(() => {
		const apps = Array.from(new Set(data.map((p) => p.application))).sort();
		return [
			{ value: 'all', label: 'All applications' },
			...apps.map((a) => ({ value: a, label: a })),
		];
	}, [data]);

	const rows = useMemo<DailyRow[]>(() => {
		const cutoff = Date.now() - DATE_RANGE_DAYS[dateRange] * 24 * 60 * 60 * 1000;
		const byDay = new Map<string, DailyRow>();

		for (const point of data) {
			if (application !== 'all' && point.application !== application) continue;

			const when = new Date(point.timestamp);
			if (when.getTime() < cutoff) continue;
			if (timeOfDay !== 'all' && bucketOfHour(when.getHours()) !== timeOfDay)
				continue;
			if (dayOfWeek !== 'all' && String(when.getDay()) !== dayOfWeek) continue;

			const day = point.timestamp.slice(0, 10); // YYYY-MM-DD
			const row = byDay.get(day) ?? {
				date: day,
				sent: 0,
				delivered: 0,
				failed: 0,
			};
			row.sent += point.sent;
			row.delivered += point.delivered;
			row.failed += point.failed;
			byDay.set(day, row);
		}

		return Array.from(byDay.values()).sort((a, b) =>
			a.date.localeCompare(b.date),
		);
	}, [data, timeOfDay, dayOfWeek, dateRange, application]);

	const fmtDate = (iso: string) => iso.slice(5); // MM-DD

	return (
		<Card className="rounded-lg border-border p-5 shadow-none lg:col-span-2">
			<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
				<h2 className="font-serif text-base font-semibold text-foreground">
					Volume and Health
				</h2>
				<div className="flex flex-wrap items-center gap-2">
					<FilterSelect
						label="Filter by time of day"
						value={timeOfDay}
						onChange={setTimeOfDay}
						options={TIME_OF_DAY_OPTIONS}
					/>
					<FilterSelect
						label="Filter by day of week"
						value={dayOfWeek}
						onChange={setDayOfWeek}
						options={DAY_OF_WEEK_OPTIONS}
					/>
					<FilterSelect
						label="Filter by date range"
						value={dateRange}
						onChange={setDateRange}
						options={DATE_RANGE_OPTIONS}
					/>
					<FilterSelect
						label="Filter by application"
						value={application}
						onChange={setApplication}
						options={applicationOptions}
					/>
				</div>
			</div>

			{rows.length === 0 ? (
				<div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
					No samples match the selected filters.
				</div>
			) : (
				<div ref={ref}>
					<ResponsiveContainer width="100%" height={260}>
						<LineChart
							data={rows}
							margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
						>
							<XAxis
								dataKey="date"
								tickFormatter={fmtDate}
								tick={AXIS_TICK}
								axisLine={false}
								tickLine={false}
								minTickGap={24}
							/>
							<Tooltip
								contentStyle={TOOLTIP_STYLE}
								labelFormatter={(l: string) => fmtDate(l)}
								cursor={{ stroke: 'var(--border-strong)' }}
							/>
							<Legend
								iconType="circle"
								verticalAlign="top"
								align="left"
								wrapperStyle={{
									fontSize: 12,
									color: 'var(--fg-muted)',
									paddingBottom: 12,
								}}
							/>
							<Line
								type="monotone"
								dataKey="sent"
								name="Sent"
								stroke={METRIC_COLORS.sent}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 4 }}
								isAnimationActive={inView}
							/>
							<Line
								type="monotone"
								dataKey="delivered"
								name="Delivered"
								stroke={METRIC_COLORS.delivered}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 4 }}
								isAnimationActive={inView}
							/>
							<Line
								type="monotone"
								dataKey="failed"
								name="Failed"
								stroke={METRIC_COLORS.failed}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 4 }}
								isAnimationActive={inView}
							/>
						</LineChart>
					</ResponsiveContainer>
				</div>
			)}
		</Card>
	);
}

function FilterSelect({
	label,
	value,
	onChange,
	options,
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	options: readonly { value: string; label: string }[];
}) {
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger aria-label={label} className="h-8 w-auto min-w-[8rem] bg-card">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{options.map((opt) => (
					<SelectItem key={opt.value} value={opt.value}>
						{opt.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}
